import { useState } from "react";
import { Lightbulb, HelpCircle, Check, X, Sparkles, Loader2, Compass } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Scroller, goldBtn } from "@/pages/_shared";

const MODULES = ["", "invoices", "quotes", "jobs", "leads", "suppliers", "team", "general"];
const WHY_LABEL = { personal: "Your preferences", company: "Your company's history & policy",
  group: "Authorised group strategy", context: "This task", collective: "Anonymised platform pattern" };

function OptionCard({ opt, onChoose, decided }) {
  const [showWhy, setShowWhy] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-charcoal p-5" data-testid={`rec-opt-${opt.approach_type}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-heading font-semibold text-white/90">{opt.title}</h4>
            {opt.exploration && <span className="flex items-center gap-1 rounded-full border border-gold/30 px-2 py-0.5 text-[10px] uppercase text-gold"><Compass className="h-3 w-3" />new idea</span>}
          </div>
          <p className="mt-1 text-sm text-white/55">{opt.summary}</p>
        </div>
        {opt.evidence?.your_accept_rate != null && (
          <span className="shrink-0 rounded-lg border border-emerald-400/20 bg-emerald-500/[0.06] px-2 py-1 text-[11px] text-emerald-200">
            {Math.round(opt.evidence.your_accept_rate * 100)}% your fit
          </span>
        )}
      </div>

      <button onClick={() => setShowWhy((s) => !s)} className="mt-3 flex items-center gap-1.5 text-xs text-gold/80 hover:text-gold" data-testid={`rec-why-${opt.approach_type}`}>
        <HelpCircle className="h-3.5 w-3.5" /> Why am I seeing this?
      </button>
      {showWhy && (
        <ul className="mt-2 space-y-1 rounded-xl border border-white/8 bg-graphite p-3 text-xs text-white/60" data-testid={`rec-why-body-${opt.approach_type}`}>
          {Object.entries(opt.why || {}).map(([k, v]) => (
            <li key={k}><span className="text-white/40">{WHY_LABEL[k] || k}:</span> {v}</li>
          ))}
        </ul>
      )}

      {!decided ? (
        <div className="mt-4 flex gap-2">
          <button onClick={() => onChoose(opt, "accepted")} className={`${goldBtn} !py-2`} data-testid={`rec-accept-${opt.approach_type}`}>
            <Check className="h-4 w-4" /> Use this
          </button>
          <button onClick={() => onChoose(opt, "rejected")} className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm text-white/60 hover:border-red-400/30 hover:text-red-200" data-testid={`rec-reject-${opt.approach_type}`}>
            <X className="h-4 w-4" /> Not for me
          </button>
        </div>
      ) : (
        <p className="mt-3 text-sm text-emerald-300">✓ Recorded — BDVG AI will use this to improve future suggestions.</p>
      )}
    </div>
  );
}

export default function Decisions() {
  const [goal, setGoal] = useState("");
  const [module, setModule] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [decided, setDecided] = useState({});

  const getOptions = async () => {
    if (!goal.trim()) return;
    setLoading(true); setDecided({});
    try {
      const { data } = await api.post("/me/recommendations", { goal, module: module || null });
      setResult(data);
    } catch (e) { toast.error("Couldn't generate options."); }
    finally { setLoading(false); }
  };

  const choose = async (opt, outcome) => {
    setDecided((d) => ({ ...d, [opt.approach_type]: outcome }));
    const others = result.options.filter((o) => o.approach_type !== opt.approach_type).map((o) => o.approach_type);
    try {
      await api.post("/me/recommendations/outcome", {
        recommendation_id: result.id, approach_type: opt.approach_type, outcome,
        goal: result.goal, selected_over: outcome === "accepted" ? others : null });
      toast.success(outcome === "accepted" ? "Great — noted your choice." : "Noted — I'll suggest differently.");
    } catch (e) { toast.error("Couldn't record that."); }
  };

  return (
    <Scroller>
      <PageHeader title="Decision support" icon={Lightbulb}
        subtitle="Describe a decision and BDVG AI offers genuinely different options — you stay in control." />
      <div className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        <div className="rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/[0.05] to-transparent p-5">
          <label className="text-sm text-white/80">What do you need to decide or get done?</label>
          <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={2}
            placeholder="e.g. How should I chase this overdue invoice? / Best way to follow up a new lead?"
            className="mt-2 w-full rounded-xl border border-white/10 bg-black px-3.5 py-2.5 text-sm focus:border-gold/40 focus:outline-none" data-testid="rec-goal-input" />
          <div className="mt-3 flex items-center gap-2">
            <select value={module} onChange={(e) => setModule(e.target.value)} className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white/70 focus:outline-none" data-testid="rec-module-select">
              {MODULES.map((m) => <option key={m} value={m}>{m ? m[0].toUpperCase() + m.slice(1) : "Any area"}</option>)}
            </select>
            <button onClick={getOptions} disabled={loading || !goal.trim()} className={`${goldBtn} disabled:opacity-40`} data-testid="rec-generate">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Get options
            </button>
          </div>
        </div>

        {result && (
          <div className="mt-6 space-y-4" data-testid="rec-results">
            <p className="text-xs uppercase tracking-wider text-white/35">{result.options.length} options for: {result.goal}</p>
            {result.options.map((o) => (
              <OptionCard key={o.id} opt={o} decided={decided[o.approach_type]} onChoose={choose} />
            ))}
          </div>
        )}
      </div>
    </Scroller>
  );
}
